'use client';

import { TrackedButton } from '@/components/ui/tracked-button';
import { cn } from '@/lib/cn';
import { X } from 'lucide-react';
import type { ReactElement } from 'react';

import type { AwarenessCardProps } from './awareness-card.types';

/**
 * Reusable feature-awareness card: an illustrated header (with an optional close
 * button), a title + body, and a footer action row. Presentational only — the
 * consumer owns visibility, the floating shell (e.g. a `Popover`), and "seen"
 * persistence.
 *
 * Modelled on the dictation "New in this release" card; created as a shared
 * component so new awareness surfaces don't hand-roll the same chrome.
 */
export function AwarenessCard({
  image,
  title,
  children,
  actions,
  onClose,
  closeLabel = 'Close',
  closeButtonActionTrackingName,
  closeButtonClassName,
  className,
}: AwarenessCardProps): ReactElement {
  return (
    <div
      className={cn(
        'flex w-full max-w-[280px] flex-col overflow-hidden rounded-xl bg-surface-container shadow-md',
        className
      )}
    >
      <div className="relative">
        {image}
        {onClose ? (
          <TrackedButton
            type="button"
            variant="ghost"
            size="xxs"
            iconOnly
            flush="all"
            className={cn('absolute end-2 top-2 text-text-primary', closeButtonClassName)}
            aria-label={closeLabel}
            onClick={onClose}
            actionTrackingName={closeButtonActionTrackingName}
          >
            <X />
          </TrackedButton>
        ) : null}
      </div>
      <div className="flex flex-col gap-4 px-4 pb-4 pt-2">
        <div className="flex flex-col gap-1">
          {title}
          {children}
        </div>
        <div className="flex items-center justify-end gap-2">{actions}</div>
      </div>
    </div>
  );
}
